"use client";

import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import {
  AlertTriangle,
  BookOpen,
  ChevronDown,
  ChevronRight,
  CreditCard,
  LineChart,
  Plus,
  RotateCcw,
  Table2,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CalculatorTemplate } from "@/components/calculator/CalculatorTemplate";
import { ResultCard } from "@/components/calculator/ResultCard";
import { LineChartWrapper } from "@/components/calculator/ChartWrapperLazy";
import { ExplanationTable } from "@/components/calculator/ExplanationTable";
import { ShareButtons } from "@/components/calculator/ShareButtons";
import { Input } from "@/components/ui/input";
import { formatCurrency } from "@/lib/utils/currency";
import {
  type Debt,
  calculateMultiDebtPayoff,
  getMinimumPaymentWarnings,
  type MultiDebtResult,
} from "@/lib/calculators/debt-payoff";

const MAX_AMOUNT = 999_999_999_999;

const debtSchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Name required"),
  balance: z.coerce
    .number()
    .min(0.01, "Enter a positive amount")
    .max(MAX_AMOUNT, "Maximum is $999 trillion"),
  apr: z.coerce.number().min(0, "Enter a valid rate").max(50),
  minimumPayment: z.coerce
    .number()
    .min(0.01, "Enter at least $0.01")
    .max(MAX_AMOUNT),
});

const schema = z.object({
  debts: z.array(debtSchema).min(1, "Add at least one debt"),
  extraPayment: z.coerce.number().min(0, "Must be 0 or more").max(MAX_AMOUNT),
});

type FormValues = z.infer<typeof schema>;

function createDebt(overrides?: Partial<Debt>): Debt {
  return {
    id: crypto.randomUUID(),
    name: "Debt",
    balance: 5000,
    apr: 18,
    minimumPayment: 200,
    ...overrides,
  };
}

const defaultValues: FormValues = {
  debts: [
    createDebt({ name: "Credit card", balance: 5000, apr: 22, minimumPayment: 200 }),
    createDebt({ name: "Auto loan", balance: 15000, apr: 7, minimumPayment: 350 }),
  ],
  extraPayment: 50,
};

const SNOWBALL_COLOR = "#2563eb";
const AVALANCHE_COLOR = "#dc2626";

function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

/** Sample chart data to ~24 points for readability */
function sampleChartData<T extends { month: number }>(
  data: T[],
  keys: (keyof T)[]
): T[] {
  if (data.length <= 25) return data;
  const step = Math.ceil(data.length / 24);
  const sampled: T[] = [];
  for (let i = 0; i < data.length; i += step) {
    sampled.push(data[i]);
  }
  if (
    data.length > 0 &&
    sampled[sampled.length - 1]?.month !== data[data.length - 1].month
  ) {
    sampled.push(data[data.length - 1]);
  }
  return sampled;
}

/** Merge snowball and avalanche chart data by month for dual-line chart */
function mergeChartData(result: MultiDebtResult): {
  month: number;
  label: string;
  snowball: number;
  avalanche: number;
}[] {
  const snow = result.snowball.chartData;
  const ava = result.avalanche.chartData;
  const maxMonth = Math.max(
    snow[snow.length - 1]?.month ?? 0,
    ava[ava.length - 1]?.month ?? 0
  );
  const snowByMonth = new Map(snow.map((d) => [d.month, d.balance]));
  const avaByMonth = new Map(ava.map((d) => [d.month, d.balance]));

  const merged: {
    month: number;
    label: string;
    snowball: number;
    avalanche: number;
  }[] = [];
  for (let m = 0; m <= maxMonth; m++) {
    merged.push({
      month: m,
      label:
        m <= 12 ? `Month ${m}` : `Year ${(m / 12).toFixed(1)}`,
      snowball: snowByMonth.get(m) ?? 0,
      avalanche: avaByMonth.get(m) ?? 0,
    });
  }
  return merged;
}

function parseValuesFromSearchParams(
  params: URLSearchParams
): Partial<FormValues> | null {
  const debtsJson = params.get("debts");
  const extra = params.get("extra");
  if (!debtsJson) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(debtsJson)) as Debt[];
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    const debts = parsed.map((d) => ({
      ...d,
      id: d.id || crypto.randomUUID(),
    }));
    return {
      debts,
      extraPayment: extra ? parseFloat(extra) : 0,
    };
  } catch {
    return null;
  }
}

function formatDebtFreeDate(payoffMonth: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + payoffMonth);
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function buildShareUrl(values: FormValues): string {
  const params = new URLSearchParams();
  params.set(
    "debts",
    encodeURIComponent(
      JSON.stringify(
        values.debts.map(({ id, name, balance, apr, minimumPayment }) => ({
          id,
          name,
          balance,
          apr,
          minimumPayment,
        }))
      )
    )
  );
  params.set("extra", String(values.extraPayment));
  if (typeof window !== "undefined") {
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }
  return "";
}

export function DebtPayoffCalculator() {
  const mounted = useMounted();
  const [formValues, setFormValues] = useState<FormValues>(defaultValues);
  const [resetKey, setResetKey] = useState(0);
  const [result, setResult] = useState<MultiDebtResult>(() =>
    calculateMultiDebtPayoff({
      debts: defaultValues.debts,
      strategy: "snowball",
      extraPayment: defaultValues.extraPayment,
    })
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const parsed = parseValuesFromSearchParams(params);
    if (parsed) {
      const merged: FormValues = {
        debts: parsed.debts ?? defaultValues.debts,
        extraPayment: parsed.extraPayment ?? defaultValues.extraPayment,
      };
      const parsedResult = schema.safeParse(merged);
      if (parsedResult.success) {
        setFormValues(parsedResult.data);
        setResult(
          calculateMultiDebtPayoff({
            debts: parsedResult.data.debts,
            strategy: "snowball",
            extraPayment: parsedResult.data.extraPayment,
          })
        );
        setResetKey((k) => k + 1);
      }
    }
  }, []);

  const recalc = useCallback((values: FormValues) => {
    setResult(
      calculateMultiDebtPayoff({
        debts: values.debts,
        strategy: "snowball",
        extraPayment: values.extraPayment,
      })
    );
  }, []);

  const handleReset = useCallback(() => {
    setFormValues(defaultValues);
    setResult(
      calculateMultiDebtPayoff({
        debts: defaultValues.debts,
        strategy: "snowball",
        extraPayment: defaultValues.extraPayment,
      })
    );
    setResetKey((k) => k + 1);
  }, []);

  const handleAddDebt = useCallback(() => {
    const newDebt = createDebt();
    setFormValues((prev) => {
      const next = { ...prev, debts: [...prev.debts, newDebt] };
      recalc(next);
      return next;
    });
  }, [recalc]);

  const handleRemoveDebt = useCallback(
    (id: string) => {
      setFormValues((prev) => {
        const next = prev.debts.filter((d) => d.id !== id);
        if (next.length === 0) return prev;
        const nextValues = { ...prev, debts: next };
        recalc(nextValues);
        return nextValues;
      });
    },
    [recalc]
  );

  const handleDebtChange = useCallback(
    (id: string, field: keyof Debt, value: string | number) => {
      setFormValues((prev) => {
        const debts = prev.debts.map((d) =>
          d.id === id ? { ...d, [field]: value } : d
        );
        const next = { ...prev, debts };
        recalc(next);
        return next;
      });
    },
    [recalc]
  );

  const [showAmortization, setShowAmortization] = useState(false);
  const [amortizationMonthsShown, setAmortizationMonthsShown] = useState(36);

  const handleExtraChange = useCallback(
    (value: number) => {
      setFormValues((prev) => {
        const next = { ...prev, extraPayment: value };
        recalc(next);
        return next;
      });
    },
    [recalc]
  );

  const mergedChart = mergeChartData(result);
  const sampled = sampleChartData(mergedChart, ["snowball", "avalanche"]);
  const chartData = sampled.map((d) => ({
    ...d,
    snowball: Math.round(d.snowball * 100) / 100,
    avalanche: Math.round(d.avalanche * 100) / 100,
  }));

  const snow = result.snowball;
  const ava = result.avalanche;
  const snowPayoff =
    snow.payoffYears < 1
      ? `${snow.payoffMonth} months`
      : `${snow.payoffYears.toFixed(1)} years`;
  const avaPayoff =
    ava.payoffYears < 1
      ? `${ava.payoffMonth} months`
      : `${ava.payoffYears.toFixed(1)} years`;

  const totalDebt = formValues.debts.reduce((s, d) => s + d.balance, 0);
  const totalMin = formValues.debts.reduce((s, d) => s + d.minimumPayment, 0);
  const minPaymentWarnings = getMinimumPaymentWarnings(formValues.debts);
  const snowDebtFree = formatDebtFreeDate(snow.payoffMonth);
  const avaDebtFree = formatDebtFreeDate(ava.payoffMonth);

  return (
    <CalculatorTemplate
      chartSectionLabel="Balance over time"
      chartSectionIcon={LineChart}
      explanationSectionLabel="Formula & examples"
      explanationSectionIcon={BookOpen}
      inputsSection={
        <div className="space-y-4">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium">Debts</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddDebt}
                className="text-xs"
              >
                <Plus className="size-3.5" />
                Add debt
              </Button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[400px] text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="px-3 py-2 text-left font-medium text-foreground">
                      Name
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-foreground">
                      Balance
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-foreground">
                      APR %
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-foreground">
                      Min payment
                    </th>
                    <th className="w-10 px-1" />
                  </tr>
                </thead>
                <tbody>
                  {formValues.debts.map((debt) => (
                    <tr
                      key={debt.id}
                      className="border-b border-border last:border-0"
                    >
                      <td className="px-3 py-1.5">
                        <Input
                          value={debt.name}
                          onChange={(e) =>
                            handleDebtChange(debt.id, "name", e.target.value)
                          }
                          className="h-7 text-sm"
                          placeholder="Name"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Input
                          type="number"
                          value={debt.balance}
                          onChange={(e) =>
                            handleDebtChange(
                              debt.id,
                              "balance",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="h-7 text-right text-sm"
                          min={0}
                          step={1}
                          inputMode="decimal"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Input
                          type="number"
                          value={debt.apr}
                          onChange={(e) =>
                            handleDebtChange(
                              debt.id,
                              "apr",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="h-7 text-right text-sm"
                          min={0}
                          max={50}
                          step={0.5}
                          inputMode="decimal"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Input
                          type="number"
                          value={debt.minimumPayment}
                          onChange={(e) =>
                            handleDebtChange(
                              debt.id,
                              "minimumPayment",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="h-7 text-right text-sm"
                          min={0}
                          step={1}
                          inputMode="decimal"
                        />
                      </td>
                      <td className="px-1 py-1.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-7 text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoveDebt(debt.id)}
                          disabled={formValues.debts.length <= 1}
                          aria-label="Remove debt"
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {minPaymentWarnings.length > 0 && (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <p>
                  Minimum payment may not cover interest on:{" "}
                  {minPaymentWarnings.map((d) => d.name).join(", ")}. Balance
                  could grow—consider paying more.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label
              htmlFor="extra-payment"
              className="text-sm font-medium text-foreground"
            >
              Extra payment
            </label>
            <Input
              id="extra-payment"
              type="number"
              value={formValues.extraPayment}
              onChange={(e) =>
                handleExtraChange(parseFloat(e.target.value) || 0)
              }
              min={0}
              step={1}
              inputMode="decimal"
              className="max-w-[140px]"
            />
            <p className="text-xs text-muted-foreground">
              Applied to your focus debt; rolls over when that debt is paid off.
            </p>
          </div>

          <div className="flex gap-2">
            <ShareButtons shareUrl={buildShareUrl(formValues)} />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="text-muted-foreground"
            >
              <RotateCcw className="size-3.5" />
              Reset
            </Button>
          </div>
        </div>
      }
      resultsSection={
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <ResultCard
              icon={CreditCard}
              metrics={[
                {
                  label: "Snowball",
                  value: snowPayoff,
                  highlight: true,
                  tooltip: "Pay smallest balance first. Quick wins.",
                },
                {
                  label: "Debt-free by",
                  value: snowDebtFree,
                  tooltip: "Estimated date when you'll be debt-free.",
                },
                {
                  label: "Total interest",
                  value: formatCurrency(snow.totalInterestPaid),
                  tooltip: "Interest paid with snowball strategy.",
                },
              ]}
            />
            <ResultCard
              icon={CreditCard}
              metrics={[
                {
                  label: "Avalanche",
                  value: avaPayoff,
                  highlight: true,
                  tooltip: "Pay highest APR first. Saves more interest.",
                },
                {
                  label: "Debt-free by",
                  value: avaDebtFree,
                  tooltip: "Estimated date when you'll be debt-free.",
                },
                {
                  label: "Total interest",
                  value: formatCurrency(ava.totalInterestPaid),
                  tooltip: "Interest paid with avalanche strategy.",
                },
              ]}
            />
          </div>

          <div className="rounded-lg border border-border bg-muted/30">
            <button
              type="button"
              onClick={() => setShowAmortization((v) => !v)}
              className="flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left text-sm font-medium text-foreground hover:bg-muted/50"
            >
              <Table2 className="size-4 text-muted-foreground" />
              Amortization table (avalanche)
              {showAmortization ? (
                <ChevronDown className="size-4 shrink-0" />
              ) : (
                <ChevronRight className="size-4 shrink-0" />
              )}
            </button>
            {showAmortization && (
            <div className="border-t border-border">
              <div className="max-h-[300px] overflow-auto px-2 pb-2 pt-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="px-3 py-1.5 font-medium text-foreground">Month</th>
                      <th className="px-3 py-1.5 text-right font-medium text-foreground">Balance</th>
                      <th className="px-3 py-1.5 text-right font-medium text-foreground">Interest</th>
                      <th className="px-3 py-1.5 text-right font-medium text-foreground">Principal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.avalanche.amortizationData
                      .slice(0, amortizationMonthsShown)
                      .map((row) => (
                        <tr key={row.month} className="border-b border-border/50">
                          <td className="px-3 py-1.5 text-muted-foreground">{row.month}</td>
                          <td className="px-3 py-1.5 text-right text-muted-foreground">
                            {formatCurrency(row.balance)}
                          </td>
                          <td className="px-3 py-1.5 text-right text-muted-foreground">
                            {formatCurrency(row.interest)}
                          </td>
                          <td className="px-3 py-1.5 text-right text-muted-foreground">
                            {formatCurrency(row.principal)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {result.avalanche.amortizationData.length > amortizationMonthsShown && (
                <div className="border-t border-border px-3 py-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground"
                    onClick={() =>
                      setAmortizationMonthsShown((n) =>
                        Math.min(
                          n + 24,
                          result.avalanche.amortizationData.length
                        )
                      )
                    }
                  >
                    Load 24 more months
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-2 text-xs text-muted-foreground"
                    onClick={() =>
                      setAmortizationMonthsShown(
                        result.avalanche.amortizationData.length
                      )
                    }
                  >
                    Show all ({result.avalanche.amortizationData.length})
                  </Button>
                </div>
              )}
            </div>
            )}
          </div>

          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">Comparison</p>
            <p className="mt-1.5">
              With {formatCurrency(totalMin)}/month in minimums
              {formValues.extraPayment > 0 && (
                <> plus {formatCurrency(formValues.extraPayment)} extra</>
              )}
              , you&apos;ll pay off {formatCurrency(totalDebt)} in{" "}
              {avaPayoff} (avalanche) or {snowPayoff} (snowball). Avalanche
              saves {formatCurrency(snow.totalInterestPaid - ava.totalInterestPaid)}{" "}
              in interest.
            </p>
          </div>
        </div>
      }
      chartSection={
        mounted ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Total balance over time. Snowball vs avalanche—compare payoff
              speed and total interest.
            </p>
            <LineChartWrapper
              data={chartData}
              dataKeys={[
                {
                  key: "snowball",
                  color: SNOWBALL_COLOR,
                  name: "Snowball",
                },
                {
                  key: "avalanche",
                  color: AVALANCHE_COLOR,
                  name: "Avalanche",
                },
              ]}
            />
          </div>
        ) : (
          <div className="h-[300px] w-full animate-pulse rounded-lg bg-muted" />
        )
      }
      explanationSection={
        <div className="space-y-6">
          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              How debt payoff works
            </h2>
            <p className="mb-3 text-muted-foreground">
              Each payment is split between interest and principal. Interest is
              calculated on the remaining balance, so early payments are mostly
              interest. As the balance shrinks, more of each payment goes to
              principal. Extra payments go directly to principal and shorten the
              payoff time.
            </p>
          </div>

          <div>
            <h3 className="mb-2 text-base font-semibold text-foreground">
              Monthly interest
            </h3>
            <p className="mb-3 font-mono text-sm text-foreground">
              Interest = Balance × (APR ÷ 12)
            </p>
            <p className="mb-3 text-muted-foreground">
              Principal paid = Payment − Interest
            </p>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Snowball vs. avalanche
            </h2>
            <p className="mb-3 text-muted-foreground">
              <strong className="text-foreground">Snowball:</strong> Pay off
              smallest balance first for quick wins.{" "}
              <strong className="text-foreground">Avalanche:</strong> Pay off
              highest interest rate first to minimize total interest. Avalanche
              saves more money; snowball can be more motivating.
            </p>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Example
            </h2>
            <p className="mb-3 text-muted-foreground">
              $10,000 at 18% APR, $300/month:
            </p>
            <ExplanationTable
              headers={["Variable", "Value"]}
              rows={[
                ["First month interest", "10,000 × 0.18 ÷ 12 = $150"],
                ["Principal in first payment", "$300 − $150 = $150"],
                ["Balance after first month", "$9,850"],
                ["Payoff time", "~4 years"],
                ["Total interest", "~$4,400"],
              ]}
            />
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Limitations
            </h2>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>Assumes fixed rate and payment; variable rates differ.</li>
              <li>Does not account for fees or payment changes.</li>
              <li>Use for planning; verify with your lender.</li>
            </ul>
          </div>
        </div>
      }
    />
  );
}
